import React, {useMemo, useState} from 'react';
import {
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Box,
    Avatar,
    Menu,
    InputAdornment, Typography
} from '@mui/material';
import {ChevronRight, User} from 'react-feather';
import {statusColors, defaultStatus} from './mockData';

const TicketEditForm = ({ticket, setTicket, staff}) => {
    const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
    const [staffMenuAnchor, setStaffMenuAnchor] = useState(null);

    // Handle status menu
    const openStatusMenu = (event) => {
        setStatusMenuAnchor(event.currentTarget);
    };

    const closeStatusMenu = () => {
        setStatusMenuAnchor(null);
    };

    // Handle staff menu
    const openStaffMenu = (event) => {
        setStaffMenuAnchor(event.currentTarget);
    };

    const closeStaffMenu = () => {
        setStaffMenuAnchor(null);
    };

    // Update ticket status
    const updateTicketStatus = (status) => {
        setTicket({
            ...ticket,
            status,
            updatedAt: new Date().toISOString()
        });

        closeStatusMenu();
    };

    // Assign staff to ticket (now correctly handling the provider_id)
    const assignStaffToTicket = (staffMember) => {
        if (staffMember === null) {
            // Clear assignment
            setTicket({
                ...ticket,
                assigned_to: null,
                assigned_to_name: null,
                assigned_to_specialization: null,
                // For backwards compatibility
                assignedTo: null,
                updatedAt: new Date().toISOString()
            });
        } else {
            // Set assignment with provider_id and other fields
            setTicket({
                ...ticket,
                assigned_to: staffMember.provider_id,
                assigned_to_name: `${staffMember.first_name} ${staffMember.last_name}`,
                assigned_to_specialization: staffMember.specialization,
                // For backwards compatibility
                assignedTo: staffMember,
                updatedAt: new Date().toISOString()
            });
        }

        closeStaffMenu();
    };

    // Get status color
    const getStatusColor = (status) => {
        if (!status) return defaultStatus;
        return statusColors[status] || defaultStatus;
    };

    // Get initials for avatar
    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(n => n[0]).join('').toUpperCase();
    };

    // Get current assigned provider for display
    const getCurrentAssignedProvider = () => {
        // If we have assigned_to as provider_id (from database schema)
        if (ticket.assigned_to) {
            return {
                id: ticket.assigned_to,
                name: ticket.assigned_to_name || 'Unknown',
                specialization: ticket.assigned_to_specialization
            };
        }
        // If we have assignedTo as an object (from within the app)
        else if (ticket.assignedTo) {
            return {
                id: ticket.assignedTo.provider_id,
                name: `${ticket.assignedTo.first_name} ${ticket.assignedTo.last_name}`,
                specialization: ticket.assignedTo.specialization
            };
        }
        // No provider assigned
        return null;
    };

    const formatDraftJsContent = (content) => {
        // If content is empty or undefined, return empty string
        if (!content) return '';

        // Parse the content if it's a string
        let contentState;
        if (typeof content === 'string') {
            try {
                contentState = JSON.parse(content);
            } catch (error) {
                // If not valid JSON, return the original string
                return content;
            }
        } else {
            contentState = content;
        }

        // Check if it's a valid Draft.js content state
        if (!contentState.blocks || !Array.isArray(contentState.blocks)) {
            // If not in expected format, return original content as string
            return typeof content === 'string' ? content : JSON.stringify(content);
        }

        // Extract text from blocks with appropriate line breaks
        const textBlocks = contentState.blocks.map(block => block.text || '');

        // Join blocks with line breaks
        return textBlocks.join('\n');
    };


    /**
     * Checks if a string is a Draft.js content state
     * @param {string} text - Text to check
     * @returns {boolean} True if it appears to be a Draft.js content state
     */
    const isDraftJsContent = (text) => {
        if (!text || typeof text !== 'string') return false;

        // Check for typical Draft.js structure markers
        return text.includes('"blocks"') && text.includes('"entityMap"');
    };

    // Format the description if it's a Draft.js content state
    const formattedDescription = useMemo(() => {
        if (!ticket || !ticket.description) return '';

        if (isDraftJsContent(ticket.description)) {
            return formatDraftJsContent(ticket.description);
        }

        return ticket.description;
    }, [ticket]);

    const currentProvider = getCurrentAssignedProvider();

    return (
        <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth sx={{mb: 2}}>
                    <InputLabel>Priority</InputLabel>
                    <Select
                        value={ticket.priority || 'Medium'}
                        onChange={(e) => setTicket({
                            ...ticket,
                            priority: e.target.value
                        })}
                        label="Priority"
                    >
                        <MenuItem value="High">High</MenuItem>
                        <MenuItem value="Medium">Medium</MenuItem>
                        <MenuItem value="Low">Low</MenuItem>
                    </Select>
                </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
                <Button
                    fullWidth
                    variant="outlined"
                    onClick={openStatusMenu}
                    endIcon={<ChevronRight size={16}/>}
                    sx={{
                        height: '56px',
                        borderRadius: '8px',
                        backgroundColor: getStatusColor(ticket.status).bg,
                        color: getStatusColor(ticket.status).color,
                        borderColor: getStatusColor(ticket.status).color,
                        '&:hover': {
                            backgroundColor: getStatusColor(ticket.status).bg,
                            opacity: 0.9
                        }
                    }}
                >
                    Status: {ticket.status}
                </Button>
                <Menu
                    anchorEl={statusMenuAnchor}
                    open={Boolean(statusMenuAnchor)}
                    onClose={closeStatusMenu}
                >
                    <MenuItem onClick={() => updateTicketStatus('Open')}>Open</MenuItem>
                    <MenuItem onClick={() => updateTicketStatus('In Progress')}>In Progress</MenuItem>
                    <MenuItem onClick={() => updateTicketStatus('Scheduled')}>Scheduled</MenuItem>
                    <MenuItem onClick={() => updateTicketStatus('Resolved')}>Resolved</MenuItem>
                    <MenuItem onClick={() => updateTicketStatus('Closed')}>Closed</MenuItem>
                </Menu>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
                <Button
                    fullWidth
                    variant="outlined"
                    onClick={openStaffMenu}
                    endIcon={<ChevronRight size={16}/>}
                    sx={{
                        height: '56px',
                        borderRadius: '8px',
                        justifyContent: 'flex-start'
                    }}
                >
                    {currentProvider ? (
                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                            <Avatar
                                sx={{
                                    width: 28,
                                    height: 28,
                                    fontSize: '0.75rem',
                                    mr: 1,
                                    bgcolor: '#10B981'
                                }}
                            >
                                {getInitials(currentProvider.name)}
                            </Avatar>
                            <span>
                                Assigned to {currentProvider.name}
                                {currentProvider.specialization &&
                                    <span style={{fontSize: '0.75rem', display: 'block', color: '#6B7280'}}>
                                        {currentProvider.specialization}
                                    </span>
                                }
                            </span>
                        </Box>
                    ) : (
                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                            <Avatar
                                sx={{
                                    width: 28,
                                    height: 28,
                                    fontSize: '0.75rem',
                                    mr: 1,
                                    bgcolor: '#9CA3AF'
                                }}
                            >
                                <User size={14}/>
                            </Avatar>
                            <span>Unassigned</span>
                        </Box>
                    )}
                </Button>
                <Menu
                    anchorEl={staffMenuAnchor}
                    open={Boolean(staffMenuAnchor)}
                    onClose={closeStaffMenu}
                >
                    <MenuItem onClick={() => assignStaffToTicket(null)}>
                        <em>Unassigned</em>
                    </MenuItem>
                    {staff.map(staffMember => (
                        <MenuItem
                            key={staffMember.provider_id}
                            onClick={() => assignStaffToTicket(staffMember)}
                        >
                            <Box sx={{display: 'flex', alignItems: 'center'}}>
                                <Avatar
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        fontSize: '0.7rem',
                                        mr: 1,
                                        bgcolor: '#10B981'
                                    }}
                                >
                                    {getInitials(`${staffMember.first_name} ${staffMember.last_name}`)}
                                </Avatar>
                                <span>
                                    {`${staffMember.first_name} ${staffMember.last_name}`}
                                    {staffMember.specialization &&
                                        <span style={{fontSize: '0.75rem', display: 'block', color: '#6B7280'}}>
                                            {staffMember.specialization}
                                        </span>
                                    }
                                </span>
                            </Box>
                        </MenuItem>
                    ))}
                </Menu>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
                <TextField
                    fullWidth
                    label="Schedule Date & Time"
                    type="datetime-local"
                    value={ticket.scheduledDate ? new Date(ticket.scheduledDate).toISOString().slice(0, 16) :
                        ticket.scheduled_date ? new Date(ticket.scheduled_date).toISOString().slice(0, 16) : ''}
                    onChange={(e) => setTicket({
                        ...ticket,
                        scheduledDate: e.target.value ? new Date(e.target.value).toISOString() : null,
                        scheduled_date: e.target.value ? new Date(e.target.value).toISOString() : null
                    })}
                    InputLabelProps={{shrink: true}}
                />
            </Grid>

            <Grid item xs={12} style={{cursor: 'none'}}>
                <Typography variant="body2">
                    {formattedDescription.split('\n').map((paragraph, index) => (
                        <Typography key={index} variant="body2"
                                    paragraph={index < formattedDescription.split('\n').length - 1}>
                            {paragraph || '\u00A0'}
                        </Typography>
                    )) || "No description provided"}</Typography>
            </Grid>

            {(ticket.status === 'Resolved' || ticket.status === 'Closed' ||
                ticket.status === 'RESOLVED' || ticket.status === 'CLOSED') && (
                <Grid item xs={12} sm={6} md={4}>
                    <TextField
                        fullWidth
                        label="Cost ($)"
                        type="number"
                        value={ticket.actualCost || ticket.actual_cost || ''}
                        onChange={(e) => {
                            const value = e.target.value ? Number(e.target.value) : null;
                            setTicket({
                                ...ticket,
                                actualCost: value,
                                actual_cost: value
                            });
                        }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                    />
                </Grid>
            )}
        </Grid>
    );
};

export default TicketEditForm;